import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Rating,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Business as CorporateIcon,
  Groups as AcademyIcon,
  Leaderboard as LeaderboardIcon,
  Refresh as RefreshIcon,
  School as SchoolIcon,
  SportsScore as ContestIcon,
} from '@mui/icons-material';
import api from '@services/api';

type TargetType = 'skill' | 'drill' | 'fitness';
type ParticipantType = 'individual' | 'corporate' | 'academy' | 'school';
type SubmissionStatus = 'submitted' | 'under_review' | 'approved' | 'rejected';

interface TargetItem {
  type: TargetType;
  code: string;
  name: string;
  sequence: number;
}

interface VideoItem {
  title: string;
  url: string;
  name?: string;
  notes?: string;
  isActive?: boolean;
}

interface ContestTask {
  id: string;
  sport: string;
  title: string;
  description?: string;
  instructions?: string;
  targetItems?: TargetItem[];
  participationFormat?: string;
  regularTrainingVideos?: VideoItem[];
  creatorReferenceVideos?: VideoItem[];
  amateurSampleVideos?: VideoItem[];
  performanceSubmissionEnabled: boolean;
  scoringNotes?: string;
  showOnHomepage: boolean;
  showOnRankingContest: boolean;
  isActive: boolean;
  displayOrder: number;
}

interface MasterOption {
  type: TargetType;
  code: string;
  name: string;
}

interface ContestSubmission {
  id: string;
  contestTaskId: string;
  contestTitle: string;
  sport: string;
  participantType: ParticipantType;
  participantName?: string;
  organizationName?: string;
  organizationLogoUrl?: string;
  contestCategory?: string;
  videoUrl: string;
  caption?: string;
  status: SubmissionStatus;
  score?: number | string | null;
  rating?: number | null;
  feedback?: string;
  adminNotes?: string;
  createdAt: string;
  reviewedAt?: string | null;
  selfAttempts?: number | string | null;
  selfSuccesses?: number | string | null;
}

const SPORTS = ['Badminton', 'Cricket', 'Tennis', 'Football', 'Athletics', 'Ice Hockey'];
const participantTabs: Array<{ value: ParticipantType; label: string; icon: React.ReactElement; color: string }> = [
  { value: 'individual', label: 'Individual', icon: <ContestIcon />, color: '#2563eb' },
  { value: 'corporate', label: 'Corporate', icon: <CorporateIcon />, color: '#7c3aed' },
  { value: 'academy', label: 'Academy', icon: <AcademyIcon />, color: '#059669' },
  { value: 'school', label: 'School', icon: <SchoolIcon />, color: '#d97706' },
];
const leaderboardCategories = ['U9', 'U11', 'U13', 'U15', 'U17', 'U19', 'Adults', 'Men', 'Women'];

const emptyForm = {
  title: '',
  description: '',
  instructions: '',
  participationFormat: 'Individual, Corporate, Academy, School',
  scoringNotes: 'Score out of 100. Rating 1-5 based on technique, consistency, accuracy, and match relevance.',
  regularVideosText: '',
  creatorVideosText: '',
  amateurVideosText: '',
  performanceSubmissionEnabled: true,
  showOnHomepage: true,
  showOnRankingContest: true,
  isActive: true,
  displayOrder: 0,
};

const videosToText = (videos?: VideoItem[]) =>
  (videos || []).map((v) => [v.title, v.url, v.name || '', v.notes || ''].join(' | ')).join('\n');

const textToVideos = (text: string): VideoItem[] =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [title = '', url = '', name = '', notes = ''] = line.split('|').map((part) => part.trim());
      return { title: title || url, url, name, notes, isActive: true };
    })
    .filter((item) => item.url);

const statusColor = (status: SubmissionStatus) => {
  if (status === 'approved') return 'success';
  if (status === 'rejected') return 'error';
  if (status === 'under_review') return 'warning';
  return 'info';
};

const AdminContestTasks: React.FC<{ sport: string }> = ({ sport }) => {
  const selectedSport = sport || 'All Sports';
  const [activeParticipantType, setActiveParticipantType] = useState<ParticipantType>('individual');
  const [leaderboardCategory, setLeaderboardCategory] = useState('U15');
  const [tasks, setTasks] = useState<ContestTask[]>([]);
  const [submissions, setSubmissions] = useState<ContestSubmission[]>([]);
  const [masterOptions, setMasterOptions] = useState<MasterOption[]>([]);
  const [selectedTargets, setSelectedTargets] = useState<TargetItem[]>([]);
  const [selectedOptionKey, setSelectedOptionKey] = useState('');
  const [form, setForm] = useState({ ...emptyForm });
  const [formSport, setFormSport] = useState(selectedSport === 'All Sports' ? 'Badminton' : selectedSport);
  const [editing, setEditing] = useState<ContestTask | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reviewing, setReviewing] = useState<ContestSubmission | null>(null);
  const [reviewForm, setReviewForm] = useState({
    status: 'under_review' as SubmissionStatus,
    score: '',
    rating: 0,
    feedback: '',
    adminNotes: '',
    contestCategory: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const apiSport = selectedSport === 'All Sports' ? 'All' : selectedSport;
  const taskSport = formSport || (selectedSport === 'All Sports' ? 'Badminton' : selectedSport);

  const selectedOption = useMemo(
    () => masterOptions.find((item) => `${item.type}:${item.code}` === selectedOptionKey),
    [masterOptions, selectedOptionKey]
  );

  const filteredSubmissions = useMemo(
    () => submissions.filter((item) => item.participantType === activeParticipantType),
    [submissions, activeParticipantType]
  );

  const leaderboard = useMemo(() => {
    const selectedCategory = leaderboardCategory.toLowerCase();
    return submissions
        .filter((item) => {
          const category = String(item.contestCategory || '').toLowerCase();
          if (selectedCategory === 'men') return category === 'men' || category.includes(' men ');
          if (selectedCategory === 'women') return category.includes('women') || category.includes('girls');
          if (selectedCategory === 'adults') return category.includes('adult');
          return category.includes(selectedCategory);
        })
        .filter((item) => item.status === 'approved' && item.score !== null && item.score !== undefined)
        .sort((a, b) => Number(b.score || 0) - Number(a.score || 0))
        .slice(0, 10);
  }, [submissions, leaderboardCategory]);

  const stats = useMemo(() => {
    const total = submissions.length;
    const approved = submissions.filter((item) => item.status === 'approved').length;
    const pending = submissions.filter((item) => item.status === 'submitted' || item.status === 'under_review').length;
    return {
      tasks: tasks.length,
      total,
      approved,
      pending,
    };
  }, [submissions, tasks]);

  const loadTasks = async () => {
    const sportsToLoad = selectedSport === 'All Sports' ? SPORTS : [selectedSport];
    const taskResponses = await Promise.all(
      sportsToLoad.map((sportName) => api.get(`/admin/contest-tasks?sport=${encodeURIComponent(sportName)}`))
    );
    setTasks(taskResponses.flatMap((response) => response.data?.data || []));
  };

  const loadSubmissions = async () => {
    const response = await api.get(`/admin/contest-submissions?sport=${encodeURIComponent(apiSport)}`);
    setSubmissions(response.data?.data || []);
  };

  const loadMasterOptions = async () => {
    if (selectedSport === 'All Sports') {
      setMasterOptions([]);
      return;
    }

    const [strokesRes, drillsRes, fitnessRes] = await Promise.all([
      api.get(`/admin/master-strokes?sport=${encodeURIComponent(selectedSport)}`),
      api.get(`/admin/master-drills?sport=${encodeURIComponent(selectedSport)}`),
      api.get(`/admin/master-fitness?sport=${encodeURIComponent(selectedSport)}`),
    ]);

    const strokes = (strokesRes.data?.data || []).map((item: any) => ({
      type: 'skill' as TargetType,
      code: item.code,
      name: [item.strokeType, item.strokeAction, item.shotType].filter(Boolean).join(' | ') || item.code,
    }));
    const drills = (drillsRes.data?.data || []).map((item: any) => ({
      type: 'drill' as TargetType,
      code: item.drillCode,
      name: item.drillName || item.drillCode,
    }));
    const fitness = (fitnessRes.data?.data || []).map((item: any) => ({
      type: 'fitness' as TargetType,
      code: item.fitnessCode,
      name: item.fitnessName || item.fitnessCode,
    }));

    setMasterOptions([...strokes, ...drills, ...fitness].filter((item) => item.code));
  };

  const loadAll = async () => {
    try {
      setLoading(true);
      setError(null);
      await Promise.all([loadTasks(), loadSubmissions(), loadMasterOptions()]);
    } catch (e: any) {
      setError(e?.response?.data?.message || e.message || 'Failed to load contest admin data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadAll();
  }, [selectedSport]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setFormSport(selectedSport === 'All Sports' ? 'Badminton' : selectedSport);
    setSelectedTargets([]);
    setSelectedOptionKey('');
    setDialogOpen(true);
  };

  const openEdit = (task: ContestTask) => {
    setEditing(task);
    setFormSport(task.sport || (selectedSport === 'All Sports' ? 'Badminton' : selectedSport));
    setForm({
      title: task.title || '',
      description: task.description || '',
      instructions: task.instructions || '',
      participationFormat: task.participationFormat || '',
      scoringNotes: task.scoringNotes || '',
      regularVideosText: videosToText(task.regularTrainingVideos),
      creatorVideosText: videosToText(task.creatorReferenceVideos),
      amateurVideosText: videosToText(task.amateurSampleVideos),
      performanceSubmissionEnabled: Boolean(task.performanceSubmissionEnabled),
      showOnHomepage: Boolean(task.showOnHomepage),
      showOnRankingContest: Boolean(task.showOnRankingContest),
      isActive: Boolean(task.isActive),
      displayOrder: Number(task.displayOrder) || 0,
    });
    setSelectedTargets(task.targetItems || []);
    setDialogOpen(true);
  };

  const addSelectedTarget = () => {
    if (!selectedOption) return;
    const exists = selectedTargets.some((item) => item.type === selectedOption.type && item.code === selectedOption.code);
    if (exists) return;
    setSelectedTargets([...selectedTargets, { ...selectedOption, sequence: selectedTargets.length + 1 }]);
    setSelectedOptionKey('');
  };

  const saveTask = async () => {
    if (!form.title.trim()) {
      setError('Title is required');
      return;
    }

    const payload = {
      sport: taskSport,
      title: form.title.trim(),
      description: form.description.trim(),
      instructions: form.instructions.trim(),
      targetItems: selectedTargets,
      participationFormat: form.participationFormat.trim(),
      regularTrainingVideos: textToVideos(form.regularVideosText),
      creatorReferenceVideos: textToVideos(form.creatorVideosText),
      amateurSampleVideos: textToVideos(form.amateurVideosText),
      performanceSubmissionEnabled: form.performanceSubmissionEnabled,
      scoringNotes: form.scoringNotes.trim(),
      showOnHomepage: form.showOnHomepage,
      showOnRankingContest: form.showOnRankingContest,
      isActive: form.isActive,
      displayOrder: Number(form.displayOrder) || 0,
    };

    try {
      if (editing) {
        await api.put(`/admin/contest-tasks/${editing.id}`, payload);
      } else {
        await api.post('/admin/contest-tasks', payload);
      }
      setSuccess(editing ? 'Contest updated' : 'Contest created');
      setDialogOpen(false);
      await loadAll();
    } catch (e: any) {
      setError(e?.response?.data?.message || e.message || 'Failed to save contest');
    }
  };

  const openReview = (submission: ContestSubmission) => {
    setReviewing(submission);
    setReviewForm({
      status: submission.status || 'under_review',
      score: submission.score === null || submission.score === undefined ? '' : String(submission.score),
      rating: Number(submission.rating || 0),
      feedback: submission.feedback || '',
      adminNotes: submission.adminNotes || '',
      contestCategory: submission.contestCategory || '',
    });
  };

  const saveReview = async () => {
    if (!reviewing) return;
    try {
      await api.put(`/admin/contest-submissions/${reviewing.id}/review`, {
        sport: reviewing.sport,
        status: reviewForm.status,
        score: reviewForm.score,
        rating: reviewForm.rating || null,
        feedback: reviewForm.feedback,
        adminNotes: reviewForm.adminNotes,
        contestCategory: reviewForm.contestCategory || null,
      });
      setSuccess('Submission review saved');
      setReviewing(null);
      await loadSubmissions();
    } catch (e: any) {
      setError(e?.response?.data?.message || e.message || 'Failed to save review');
    }
  };

  return (
    <Box>
      <Paper
        sx={{
          p: 3,
          mb: 3,
          overflow: 'hidden',
          position: 'relative',
          color: '#fff',
          background: 'radial-gradient(circle at 12% 20%, rgba(255,255,255,0.24), transparent 24%), linear-gradient(135deg, #172554 0%, #6d28d9 48%, #db2777 100%)',
        }}
      >
        <Box sx={{ position: 'absolute', width: 220, height: 220, borderRadius: '50%', right: -60, top: -70, border: '32px solid rgba(255,255,255,0.16)' }} />
        <Box sx={{ position: 'absolute', width: 180, height: 180, right: 120, bottom: -120, transform: 'rotate(25deg)', bgcolor: 'rgba(255,255,255,0.12)', borderRadius: 6 }} />
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ position: 'relative' }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 950 }}>
              Contests
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.7, maxWidth: 780, color: 'rgba(255,255,255,0.86)' }}>
              Create contests for every sport, verify submissions, rate entries, and publish leaderboard-ready results across individuals, corporates, academies, and schools.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ bgcolor: '#fff', color: '#172554', fontWeight: 900, '&:hover': { bgcolor: '#e0f2fe' } }}>
              Create Contest
            </Button>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadAll} sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.7)' }}>
              Refresh
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>Submission Entries</Typography>
                <Chip label={`${stats.pending} pending review`} color={stats.pending ? 'warning' : 'success'} size="small" />
              </Stack>
              <Tabs
                value={activeParticipantType}
                onChange={(_, value) => setActiveParticipantType(value)}
                variant="scrollable"
                scrollButtons="auto"
                TabIndicatorProps={{ sx: { display: 'none' } }}
                sx={{ mb: 2, minHeight: 46 }}
              >
                {participantTabs.map((tab) => (
                  <Tab
                    key={tab.value}
                    value={tab.value}
                    label={tab.label}
                    icon={tab.icon}
                    iconPosition="start"
                    sx={{
                      minHeight: 42,
                      mr: 1,
                      borderRadius: 999,
                      border: `1px solid ${tab.color}33`,
                      color: tab.color,
                      fontWeight: 900,
                      textTransform: 'none',
                      bgcolor: `${tab.color}12`,
                      '&.Mui-selected': {
                        color: '#fff',
                        background: `linear-gradient(135deg, ${tab.color}, #111827)`,
                        boxShadow: `0 12px 24px ${tab.color}33`,
                      },
                    }}
                  />
                ))}
              </Tabs>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Participant</TableCell>
                      <TableCell>Contest</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Score</TableCell>
                      <TableCell>Rating</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredSubmissions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                          <Typography color="text.secondary">No {activeParticipantType} entries yet.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : filteredSubmissions.map((submission) => (
                      <TableRow key={submission.id} hover>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar
                              src={submission.organizationLogoUrl || undefined}
                              sx={{
                                width: 34,
                                height: 34,
                                bgcolor: participantTabs.find((tab) => tab.value === submission.participantType)?.color || '#2563eb',
                                fontSize: 13,
                                fontWeight: 900,
                              }}
                            >
                              {(submission.organizationName || submission.participantName || 'P').charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 800 }}>{submission.participantName || 'Participant'}</Typography>
                              <Typography variant="caption" color="text.secondary">{submission.organizationName || submission.sport}</Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>{submission.contestTitle}</TableCell>
                        <TableCell>{submission.contestCategory || '-'}</TableCell>
                        <TableCell><Chip size="small" label={submission.status.replace('_', ' ')} color={statusColor(submission.status) as any} /></TableCell>
                        <TableCell>
                          {submission.score ?? '-'}
                          {submission.selfAttempts != null && submission.selfSuccesses != null ? (
                            <Typography variant="caption" display="block" color="text.secondary">
                              Self {submission.selfSuccesses}/{submission.selfAttempts}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell><Rating size="small" value={Number(submission.rating || 0)} readOnly /></TableCell>
                        <TableCell><Button size="small" variant="contained" onClick={() => openReview(submission)}>Verify / Rate</Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #fffbeb 0%, #ffffff 42%, #eef2ff 100%)' }}>
            <CardContent>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <LeaderboardIcon color="warning" />
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>Leaderboard - {leaderboardCategory}</Typography>
                </Stack>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {leaderboardCategories.map((category) => (
                    <Chip
                      key={category}
                      label={category}
                      clickable
                      onClick={() => setLeaderboardCategory(category)}
                      color={leaderboardCategory === category ? 'warning' : 'default'}
                      variant={leaderboardCategory === category ? 'filled' : 'outlined'}
                      sx={{ fontWeight: 900 }}
                    />
                  ))}
                </Stack>
              </Stack>
              <Grid container spacing={1}>
                {leaderboard.length === 0 ? (
                  <Grid item xs={12}>
                    <Alert severity="info">No approved scored entries for {leaderboardCategory} yet.</Alert>
                  </Grid>
                ) : leaderboard.map((entry, index) => (
                  <Grid item xs={12} md={6} lg={4} key={entry.id}>
                  <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 2, height: '100%' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar src={entry.organizationLogoUrl || undefined} sx={{ width: 30, height: 30, fontSize: 12, fontWeight: 900 }}>
                          {(entry.organizationName || entry.participantName || 'P').charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 900 }}>#{index + 1} {entry.participantName}</Typography>
                          <Typography variant="caption" color="text.secondary">{entry.organizationName || entry.participantType} | {entry.sport}</Typography>
                        </Box>
                      </Stack>
                      <Chip label={entry.score} color={index === 0 ? 'warning' : 'primary'} sx={{ fontWeight: 900 }} />
                    </Stack>
                  </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>Contest List</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Contest</TableCell>
                    <TableCell>Sport</TableCell>
                    <TableCell>Participation</TableCell>
                    <TableCell>Mapped Items</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tasks.map((task) => (
                    <TableRow key={`${task.sport}-${task.id}`} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>{task.title}</Typography>
                        <Typography variant="caption" color="text.secondary">{task.description || '-'}</Typography>
                      </TableCell>
                      <TableCell>{task.sport}</TableCell>
                      <TableCell>{task.participationFormat || '-'}</TableCell>
                      <TableCell>{task.targetItems?.length || 0}</TableCell>
                      <TableCell><Chip size="small" label={task.isActive ? 'Active' : 'Inactive'} color={task.isActive ? 'success' : 'default'} /></TableCell>
                      <TableCell><Button size="small" onClick={() => openEdit(task)}>Edit</Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit Contest' : 'Create Contest'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Sport</InputLabel>
              <Select value={taskSport} label="Sport" onChange={(e) => setFormSport(String(e.target.value))}>
                {SPORTS.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} fullWidth required />
            <TextField label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} fullWidth multiline minRows={2} />
            <TextField label="Instructions" value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} fullWidth multiline minRows={2} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Manual Skill / Drill / Fitness Mapping</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField select label="Select Master Item" value={selectedOptionKey} onChange={(e) => setSelectedOptionKey(e.target.value)} fullWidth disabled={selectedSport === 'All Sports'}>
                  {masterOptions.map((item) => (
                    <MenuItem key={`${item.type}:${item.code}`} value={`${item.type}:${item.code}`}>
                      {item.type.toUpperCase()} - {item.name} ({item.code})
                    </MenuItem>
                  ))}
                </TextField>
                <Button variant="outlined" onClick={addSelectedTarget} disabled={!selectedOptionKey}>Add</Button>
              </Stack>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 1 }}>
                {selectedTargets.map((item) => (
                  <Chip key={`${item.type}-${item.code}`} label={`${item.sequence}. ${item.type}: ${item.name}`} onDelete={() => setSelectedTargets(selectedTargets.filter((target) => target !== item))} />
                ))}
              </Stack>
            </Box>
            <TextField label="Participation Format" value={form.participationFormat} onChange={(e) => setForm({ ...form, participationFormat: e.target.value })} fullWidth />
            <TextField label="Scoring Notes" value={form.scoringNotes} onChange={(e) => setForm({ ...form, scoringNotes: e.target.value })} fullWidth multiline minRows={2} />
            <Alert severity="info">Add one video per line: Title | URL | Creator/Player Name | Notes</Alert>
            <TextField label="Regular Training Videos" value={form.regularVideosText} onChange={(e) => setForm({ ...form, regularVideosText: e.target.value })} fullWidth multiline minRows={2} />
            <TextField label="Creator / Reference Videos" value={form.creatorVideosText} onChange={(e) => setForm({ ...form, creatorVideosText: e.target.value })} fullWidth multiline minRows={2} />
            <TextField label="Amateur Videos" value={form.amateurVideosText} onChange={(e) => setForm({ ...form, amateurVideosText: e.target.value })} fullWidth multiline minRows={2} />
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <FormControlLabel control={<Checkbox checked={form.performanceSubmissionEnabled} onChange={(e) => setForm({ ...form, performanceSubmissionEnabled: e.target.checked })} />} label="Enable upload" />
              <FormControlLabel control={<Checkbox checked={form.showOnRankingContest} onChange={(e) => setForm({ ...form, showOnRankingContest: e.target.checked })} />} label="Show in contest" />
              <FormControlLabel control={<Checkbox checked={form.showOnHomepage} onChange={(e) => setForm({ ...form, showOnHomepage: e.target.checked })} />} label="Show on home" />
              <FormControlLabel control={<Checkbox checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />} label="Active" />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveTask}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(reviewing)} onClose={() => setReviewing(null)} maxWidth="md" fullWidth>
        <DialogTitle>Verify and Rate Entry</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              {reviewing?.participantName || 'Participant'} | {reviewing?.contestTitle}
              {reviewing?.selfAttempts != null && reviewing?.selfSuccesses != null
                ? ` · Self-verified ${reviewing.selfSuccesses} / ${reviewing.selfAttempts}`
                : ''}
            </Alert>
            {reviewing?.videoUrl && (
              <Box sx={{ bgcolor: 'black', borderRadius: 1, overflow: 'hidden' }}>
                <video
                  controls
                  style={{ width: '100%', maxHeight: 320, display: 'block' }}
                  src={reviewing.videoUrl.startsWith('http') ? reviewing.videoUrl : `${window.location.origin}${reviewing.videoUrl}`}
                >
                  Your browser does not support video playback.
                </video>
              </Box>
            )}
            <TextField select label="Status" value={reviewForm.status} onChange={(e) => setReviewForm({ ...reviewForm, status: e.target.value as SubmissionStatus })} fullWidth>
              <MenuItem value="submitted">Submitted</MenuItem>
              <MenuItem value="under_review">Under Review</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </TextField>
            <TextField label="Score" type="number" value={reviewForm.score} onChange={(e) => setReviewForm({ ...reviewForm, score: e.target.value })} fullWidth />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 800 }}>Rating</Typography>
              <Rating value={reviewForm.rating} onChange={(_, value) => setReviewForm({ ...reviewForm, rating: value || 0 })} />
            </Box>
            <TextField select label="Age / Gender Category" value={reviewForm.contestCategory} onChange={(e) => setReviewForm({ ...reviewForm, contestCategory: e.target.value })} fullWidth>
              <MenuItem value="">— Not set —</MenuItem>
              {leaderboardCategories.map((cat) => <MenuItem key={cat} value={cat}>{cat}</MenuItem>)}
            </TextField>
            <TextField label="Feedback" value={reviewForm.feedback} onChange={(e) => setReviewForm({ ...reviewForm, feedback: e.target.value })} fullWidth multiline minRows={3} />
            <TextField label="Admin Notes" value={reviewForm.adminNotes} onChange={(e) => setReviewForm({ ...reviewForm, adminNotes: e.target.value })} fullWidth multiline minRows={2} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewing(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveReview}>Save Review</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminContestTasks;
